import type { ClientInput, VolumePrescription } from '../types';
import type { Tracer } from '../tracer';

/**
 * Volume rule — target weekly sets per muscle group, derived from
 * experience × goal. Numbers reference Schoenfeld 2017 + RP recommendations
 * and the project brief.
 */
export function volumeRule(client: ClientInput, trace: Tracer): VolumePrescription {
  const base = baseVolumeForExperience(client.experience_level);
  const adjusted = adjustForGoal(base, client.primary_goal);

  trace.emit(
    'volume',
    `${adjusted.sets_per_muscle_per_week_min}-${adjusted.sets_per_muscle_per_week_max} sets/muscle/week`,
    `Experience: ${client.experience_level}; goal: ${client.primary_goal}.`,
    {
      experience_level: client.experience_level,
      primary_goal: client.primary_goal,
    },
  );

  return adjusted;
}

function baseVolumeForExperience(level: ClientInput['experience_level']): VolumePrescription {
  switch (level) {
    case 'beginner':
      return { sets_per_muscle_per_week_min: 10, sets_per_muscle_per_week_max: 12 };
    case 'intermediate':
      return { sets_per_muscle_per_week_min: 12, sets_per_muscle_per_week_max: 18 };
    case 'advanced':
      return { sets_per_muscle_per_week_min: 16, sets_per_muscle_per_week_max: 22 };
  }
}

function adjustForGoal(
  base: VolumePrescription,
  goal: ClientInput['primary_goal'],
): VolumePrescription {
  // Muscle gain skews higher in the range; strength/endurance skew lower.
  switch (goal) {
    case 'muscle_gain':
      return base;
    case 'fat_loss':
      // Volume holds; intensity rules adjust load. Same band.
      return base;
    case 'strength':
      return {
        sets_per_muscle_per_week_min: Math.max(8, base.sets_per_muscle_per_week_min - 2),
        sets_per_muscle_per_week_max: Math.max(10, base.sets_per_muscle_per_week_max - 4),
      };
    case 'endurance':
      return {
        sets_per_muscle_per_week_min: Math.max(8, base.sets_per_muscle_per_week_min - 2),
        sets_per_muscle_per_week_max: Math.max(10, base.sets_per_muscle_per_week_max - 2),
      };
    case 'general_fitness':
      return {
        sets_per_muscle_per_week_min: Math.max(8, base.sets_per_muscle_per_week_min - 2),
        sets_per_muscle_per_week_max: base.sets_per_muscle_per_week_max - 2,
      };
  }
}

export const _internal = { baseVolumeForExperience, adjustForGoal };
